import type { ApiErrorPayload } from '../../shared/types';
import { currentAuthGeneration } from './auth-generation';
import { checkSession } from './session-check';

let sessionCheck: { generation: number; promise: Promise<void> } | undefined;

async function revalidateSession(generation: number): Promise<void> {
  if (generation !== currentAuthGeneration()) return;
  if (sessionCheck?.generation === generation) return sessionCheck.promise;
  const promise = (async () => {
    try {
      // A 401 may belong to an old cookie after another tab signs in. Check
      // the browser's current cookie before clearing its shared auth state.
      const payload = await checkSession();
      if (!payload || generation !== currentAuthGeneration()) return;
      window.dispatchEvent(new Event(payload.user ? 'auth:refresh' : 'auth:expired'));
    } catch {
      // A network failure does not prove that the current session expired.
    }
  })();
  const check = { generation, promise };
  sessionCheck = check;
  try {
    await promise;
  } finally {
    if (sessionCheck === check) sessionCheck = undefined;
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly issues: Array<{ path: string; message: string }> = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const generation = currentAuthGeneration();
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  if (!response.ok) {
    let payload: ApiErrorPayload | null;
    try {
      payload = (await response.json()) as ApiErrorPayload;
    } catch {
      payload = null;
    }
    const error = new ApiError(
      response.status,
      payload?.error.code ?? 'HTTP_ERROR',
      payload?.error.message ?? `HTTP ${response.status}`,
      payload?.error.issues ?? [],
    );
    if (response.status === 401 && path !== '/api/auth/login') {
      await revalidateSession(generation);
    }
    throw error;
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function queryString(values: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value));
  });
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
}
